import React, { useState } from 'react';

interface TransactionCardProps {
    transaction: {
        id: string;
        date: string;
        type: 'purchase' | 'usage';
        title: string;
        subtitle: string;
        amount: number;
        price?: number;
        currency?: string;
        status?: string;
        prompt?: string;
        icon: string;
        iconColor: string;
        iconBg: string;
        generationType?: string;
        isGenerationSuccessful?: boolean;
        // Additional details for expandable cards
        details?: {
            numberOfImages?: number;
            numberOfVideos?: number;
            styleName?: string;
            imageUrls?: string[];
            videoUrls?: string[];
            inputImageUrls?: string[];
        };
    };
}

const TransactionCard: React.FC<TransactionCardProps> = ({ transaction }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showAllImages, setShowAllImages] = useState(false);

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Invalid Date';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }

        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const statusConfig = {
            completed: { bg: 'bg-green-100', text: 'text-green-800' },
            pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
            failed: { bg: 'bg-red-100', text: 'text-red-800' },
            processing: { bg: 'bg-blue-100', text: 'text-blue-800' }
        };

        const config = statusConfig[status.toLowerCase() as keyof typeof statusConfig] ||
            { bg: 'bg-accent-sand', text: 'text-text-heading' };

        return (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
                {status}
            </span>
        );
    };

    const getCurrencySymbol = (currency?: string) => {
        const currencyMap: Record<string, string> = {
            'USD': '$',
            'INR': '₹',
            'EUR': '€',
            'GBP': '£'
        };

        return currencyMap[currency?.toUpperCase() || 'USD'] || '$';
    };

    const hasExpandableContent = transaction.type === 'usage' && transaction.details &&
        (transaction.details.imageUrls?.length || transaction.details.videoUrls?.length ||
            transaction.details.inputImageUrls?.length || transaction.details.styleName);

    return (
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start space-x-4">
                {/* Icon */}
                <div className={`w-10 h-10 rounded-full ${transaction.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${transaction.icon} ${transaction.iconColor}`}></i>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {/* Main Content Row */}
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div
                                className={`flex items-center justify-between ${hasExpandableContent ? 'cursor-pointer' : ''}`}
                                onClick={() => hasExpandableContent && setIsExpanded(!isExpanded)}
                            >
                                <h4 className="text-sm font-medium text-text-heading">{transaction.title}</h4>
                                {hasExpandableContent && (
                                    <button
                                        className="ml-2 p-1 text-text-description hover:text-text-description transition-colors"
                                        aria-label={isExpanded ? "Collapse details" : "Expand details"}
                                    >
                                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-xs`}></i>
                                    </button>
                                )}
                            </div>

                            {/* Basic info - always visible */}
                            {!isExpanded && transaction.type === 'purchase' && (
                                <p className="text-sm text-text-description">{transaction.subtitle}</p>
                            )}

                            {transaction.prompt && (
                                <p className="text-xs text-text-description mt-1 line-clamp-2">
                                    {transaction.prompt}
                                </p>
                            )}
                        </div>

                        {/* Credits and Price */}
                        <div className="ml-4 flex flex-col items-end space-y-1">
                            {transaction.type === 'usage' && transaction.isGenerationSuccessful === false ? (
                                // Show failed generation as refunded (no net credit loss)
                                <div className="text-right">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <span className="text-lg font-semibold text-red-600">
                                            -{transaction.amount.toFixed(2)}
                                        </span>
                                        <span className="text-sm text-text-description">credits</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm font-medium text-green-600">
                                            +{transaction.amount.toFixed(2)}
                                        </span>
                                        <span className="text-xs text-text-description">refunded</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2">
                                    <span className={`text-lg font-semibold ${transaction.type === 'purchase' ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        {transaction.type === 'purchase' ? '+' : '-'}{transaction.amount.toFixed(2)}
                                    </span>
                                    <span className="text-sm text-text-description">credits</span>
                                </div>
                            )}
                            {transaction.price !== undefined && (
                                <span className="text-sm text-text-description">
                                    {getCurrencySymbol(transaction.currency)}{transaction.price.toFixed(2)}
                                </span>
                            )}
                            {getStatusBadge(transaction.status)}
                        </div>
                    </div>

                    {/* Date */}
                    <div className="mt-2 text-xs text-text-description">
                        {formatDate(transaction.date)}
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && transaction.details && (
                        <div className="mt-4 pt-4 border-t border-accent-taupe">
                            <div className="space-y-3">
                                {/* Generation Details */}
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    {transaction.details.numberOfImages !== undefined && (
                                        <div>
                                            <span className="text-text-description">Images Generated:</span>
                                            <span className="ml-2 font-medium">{transaction.details.numberOfImages}</span>
                                        </div>
                                    )}
                                    {transaction.details.numberOfVideos !== undefined && (
                                        <div>
                                            <span className="text-text-description">Videos Generated:</span>
                                            <span className="ml-2 font-medium">{transaction.details.numberOfVideos}</span>
                                        </div>
                                    )}
                                    {transaction.details.styleName && (
                                        <div className="col-span-2">
                                            <span className="text-text-description">Style:</span>
                                            <span className="ml-2 font-medium capitalize">{transaction.details.styleName}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Media Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">

                                    {/* Input Images */}
                                    {transaction.details.inputImageUrls && transaction.details.inputImageUrls.length > 0 && (
                                        <div>
                                            <h5 className="text-sm font-medium text-text-heading mb-2">Input Images ({transaction.details.inputImageUrls.length})</h5>
                                            <div className="grid grid-cols-2 gap-2">
                                                {transaction.details.inputImageUrls.map((url, index) => (
                                                    <div key={index} className="relative group aspect-square">
                                                        <img
                                                            src={url}
                                                            alt={`Input ${index + 1}`}
                                                            className="w-full h-full object-contain rounded border hover:shadow-md transition-shadow cursor-pointer"
                                                            onClick={() => window.open(url, '_blank')}
                                                            onError={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                const parent = target.parentElement;
                                                                if (parent) {
                                                                    parent.innerHTML = '<div class="w-full h-full bg-accent-sand rounded border flex items-center justify-center"><span class="text-xs text-text-description">Image unavailable</span></div>';
                                                                }
                                                            }}
                                                        />
                                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded flex items-center justify-center pointer-events-none">
                                                            <i className="fas fa-external-link-alt text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"></i>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Generated Content */}
                                    <div>
                                        {/* Generated Videos */}
                                        {transaction.details.videoUrls && transaction.details.videoUrls.length > 0 && (
                                            <div>
                                                <h5 className="text-sm font-medium text-text-heading mb-2">Generated Videos ({transaction.details.videoUrls.length})</h5>
                                                <div className="space-y-2">
                                                    {transaction.details.videoUrls.map((url, index) => (
                                                        <a
                                                            key={index}
                                                            href={url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center text-sm text-secondary-600 hover:text-secondary-800 transition-colors"
                                                        >
                                                            <i className="fas fa-play-circle mr-2"></i>
                                                            Video {index + 1}
                                                            <i className="fas fa-external-link-alt ml-1 text-xs"></i>
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Generated Images */}
                                        {transaction.details.imageUrls && transaction.details.imageUrls.length > 0 && transaction.generationType !== 'video-generation' && (
                                            <div>
                                                <h5 className="text-sm font-medium text-text-heading mb-2">Generated Images ({transaction.details.imageUrls.length})</h5>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {(showAllImages ? transaction.details.imageUrls : transaction.details.imageUrls.slice(0, 4)).map((url, index) => (
                                                        <div key={index} className="relative group aspect-square">
                                                            <img
                                                                src={url}
                                                                alt={`Generated ${index + 1}`}
                                                                className="w-full h-full object-contain rounded border hover:shadow-md transition-shadow cursor-pointer"
                                                                onClick={() => window.open(url, '_blank')}
                                                                onError={(e) => {
                                                                    const target = e.target as HTMLImageElement;
                                                                    const parent = target.parentElement;
                                                                    if (parent) {
                                                                        parent.innerHTML = '<div class="w-full h-full bg-accent-sand rounded border flex items-center justify-center"><span class="text-xs text-text-description">Image unavailable</span></div>';
                                                                    }
                                                                }}
                                                            />
                                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded flex items-center justify-center pointer-events-none">
                                                                <i className="fas fa-external-link-alt text-white opacity-0 group-hover:opacity-100 transition-opacity"></i>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {!showAllImages && transaction.details.imageUrls.length > 4 && (
                                                        <div 
                                                            className="w-full h-full bg-accent-sand rounded border flex items-center justify-center aspect-square cursor-pointer hover:bg-accent-taupe transition-colors"
                                                            onClick={() => setShowAllImages(true)}
                                                        >
                                                            <span className="text-xs text-text-description font-medium">+{transaction.details.imageUrls.length - 4} more</span>
                                                        </div>
                                                    )}
                                                    {showAllImages && transaction.details.imageUrls.length > 4 && (
                                                        <div 
                                                            className="w-full h-full bg-accent-sand rounded border flex items-center justify-center aspect-square cursor-pointer hover:bg-accent-taupe transition-colors"
                                                            onClick={() => setShowAllImages(false)}
                                                        >
                                                            <span className="text-xs text-text-description font-medium">Show Less</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TransactionCard; 